import { useMutation } from '@apollo/client';
import {
  FULL_POINTS_REDEMPTION,
  ROLLBACK_LOYALTY_ACTIVITY,
  WAIVE_OFFLINE_FEES,
  ADD_NOTE_TO_BOOKING,
  ADD_ADDITIONAL_DETAILS
} from 'store/mutations';
import { buildPointsRedemptionLoyaltyData } from 'utils/booking';
import { getTotalPoints, getOfflineFees } from 'components/ui/BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.utils';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { OFFLINE_FEE_DETAILS_REASON, OFFLINE_FEE_DETAILS_VALUE } from 'types/fees';

const usePayIfLoyaltyAndWaiveOfflineFeesMutation = () => {
  const [redeemLoyaltyPoints, { loading: redeemLoyaltyPointsLoading, error: redeemLoyaltyPointsError }] =
    useMutation(FULL_POINTS_REDEMPTION);
  const [rollbackActivity, { loading: rollbackActivityLoading, error: rollbackActivityError }] = useMutation(ROLLBACK_LOYALTY_ACTIVITY);
  const [waiveOfflineFees, { loading: waiveOfflineFeesLoading, error: waiveOfflineFeesError }] = useMutation(WAIVE_OFFLINE_FEES);
  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);
  const [addNote, { loading: addNoteLoading }] = useMutation(ADD_NOTE_TO_BOOKING);

  const handleMutation = async ({ variables: { membershipNumber, bookingData, actionBy, reason } }) => {
    const totalPoints = getTotalPoints(bookingData);
    const { totalPriceToBePaid, fees, productFamilies } = bookingData;
    const offlineFees = getOfflineFees(fees);
    const firstOfflineFee = offlineFees?.length > 0 ? offlineFees[0] : null;
    const getFirstOfflineFeePrice = firstOfflineFee?.price || 0;
    const nothingToPayAfterWaivingOfflineFees = totalPriceToBePaid - getFirstOfflineFeePrice <= 0;
    const isLoyaltyLoggedOut =
      productFamilies?.map(({ familyGroup }) => familyGroup).includes(productFamilyCodes.EUROSTAR_LOYALTY) && !membershipNumber;
    let pointsRedemptionResult = null;

    try {
      if (isLoyaltyLoggedOut) {
        throw new Error('Loyalty account not found. Please log in on Club Eurostar first');
      }

      if (membershipNumber && totalPoints && nothingToPayAfterWaivingOfflineFees) {
        const { data } = await redeemLoyaltyPoints({
          variables: buildPointsRedemptionLoyaltyData({
            membershipNumber,
            actionBy,
            booking: bookingData,
            useNewLoyaltySystem: true
          })
        });
        pointsRedemptionResult = data.fullPointsRedemption;
      }

      const waiveOfflineFeesResponse = await waiveOfflineFees({
        variables: { bookingReference: bookingData.reference, reason }
      });

      const offlineFeeRef = offlineFees?.find(({ provisional }) => provisional)?.itemRef;

      if (offlineFeeRef) {
        await addAdditionalDetails({
          variables: {
            bookingReference: bookingData.reference,
            additionalDetails: [
              {
                itemRefId: parseInt(offlineFeeRef),
                key: OFFLINE_FEE_DETAILS_REASON,
                value: OFFLINE_FEE_DETAILS_VALUE
              }
            ]
          }
        });
      }

      if (pointsRedemptionResult) {
        try {
          await addNote({
            variables: {
              // eslint-disable-next-line max-len
              note: `Purchase of tickets for Club Eurostar account: ${membershipNumber}, totalPointsSpent: ${
                pointsRedemptionResult.pointsSpent || pointsRedemptionResult.totalPointsSpent
              }`,
              bookingReference: bookingData.reference
            }
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`Failed to add note for points redemption, booking: ${bookingData.reference}`, err);
        }
      }

      return waiveOfflineFeesResponse;
    } catch (err) {
      if (pointsRedemptionResult) {
        try {
          await rollbackActivity({
            variables: {
              activityId: pointsRedemptionResult.activityId,
              useNewLoyaltySystem: true,
              actionBy
            }
          });
        } catch (error) {
          alert('The rollback of points redemption failed, so please try this manually');
        }
      }
      throw err;
    }
  };

  return [
    handleMutation,
    {
      loading:
        redeemLoyaltyPointsLoading || waiveOfflineFeesLoading || rollbackActivityLoading || addNoteLoading || addAdditionalDetailsLoading,
      error: redeemLoyaltyPointsError || waiveOfflineFeesError || rollbackActivityError
    }
  ];
};

export default usePayIfLoyaltyAndWaiveOfflineFeesMutation;
